/**
 * Authentication utilities for WordPress/WooCommerce integration
 */

const WORDPRESS_URL = process.env.NEXT_PUBLIC_WORDPRESS_URL || process.env.WORDPRESS_URL || 'http://localhost';

export interface RegisterData {
  email: string;
  password: string;
  username?: string;
  firstName?: string;
  lastName?: string;
}

export interface LoginData {
  username: string; // Can be email or username
  password: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  user?: {
    id: number;
    email: string;
    username: string;
    displayName: string;
    firstName?: string;
    lastName?: string;
  };
  message?: string;
}

/**
 * Register a new customer using Next.js API route (recommended)
 * This proxies to WordPress/WooCommerce and creates a user in the database
 */
export async function registerCustomer(data: RegisterData): Promise<AuthResponse> {
  try {
    // Use Next.js API route to avoid CORS issues and provide secure registration
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: data.email,
        password: data.password,
        username: data.username,
        firstName: data.firstName,
        lastName: data.lastName,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      // Handle API errors
      const errorMessage = result.message || 'Registration failed';
      return {
        success: false,
        message: errorMessage,
      };
    }

    // User created successfully - try to log them in automatically
    const loginResponse = await loginCustomer({
      username: result.user?.username || result.user?.email || data.email,
      password: data.password,
    });

    if (loginResponse.success && loginResponse.token) {
      return {
        success: true,
        token: loginResponse.token,
        user: {
          id: result.user.id,
          email: result.user.email,
          username: result.user.username,
          displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || result.user.username,
          firstName: data.firstName,
          lastName: data.lastName,
        },
      };
    }

    // User created but auto-login failed - user needs to log in manually
    return {
      success: true,
      message: 'Account created successfully. Please log in.',
      user: {
        id: result.user.id,
        email: result.user.email,
        username: result.user.username,
        displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || result.user.username,
        firstName: data.firstName,
        lastName: data.lastName,
      },
    };
  } catch (error: any) {
    console.error('Registration error:', error);
    return {
      success: false,
      message: error.message || 'An error occurred during registration',
    };
  }
}

/**
 * Login customer using WordPress REST API
 * Uses custom login endpoint that doesn't require JWT plugin
 */
export async function loginCustomer(data: LoginData): Promise<AuthResponse> {
  try {
    // Use custom WordPress login endpoint (public, no JWT plugin required)
    const loginResponse = await fetch(`${WORDPRESS_URL}/wp-json/nexbeauty/v1/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: data.username, // Can be email or username
        password: data.password,
      }),
    });

    if (loginResponse.ok) {
      const result = await loginResponse.json();
      if (result.success && result.token) {
        return {
          success: true,
          token: result.token,
          user: {
            id: result.user.id,
            email: result.user.email,
            username: result.user.username,
            displayName: result.user.displayName || result.user.username,
            firstName: result.user.firstName,
            lastName: result.user.lastName,
          },
        };
      }
    }

    // If login failed, get error message
    const errorData = await loginResponse.json().catch(() => ({}));
    return {
      success: false,
      message: errorData.message || 'Invalid username or password',
    };
  } catch (error: any) {
    console.error('Login error:', error);
    return {
      success: false,
      message: error.message || 'An error occurred during login',
    };
  }
}

/**
 * Alternative: Register using WooCommerce REST API
 * This creates a customer in WooCommerce
 */
export async function registerCustomerWooCommerce(data: RegisterData): Promise<AuthResponse> {
  try {
    const response = await fetch(`${WORDPRESS_URL}/wp-json/wc/v3/customers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // WooCommerce REST API requires consumer key/secret
        // These should be set in environment variables
        'X-WC-Key': process.env.NEXT_PUBLIC_WOOCOMMERCE_CONSUMER_KEY || '',
        'X-WC-Secret': process.env.NEXT_PUBLIC_WOOCOMMERCE_CONSUMER_SECRET || '',
      },
      body: JSON.stringify({
        email: data.email,
        username: data.username || data.email.split('@')[0],
        password: data.password,
        first_name: data.firstName || '',
        last_name: data.lastName || '',
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: result.message || result.code || 'Registration failed',
      };
    }

    // WooCommerce customer creation doesn't return auth token
    // User needs to log in separately
    return {
      success: true,
      message: 'Account created successfully. Please log in.',
      user: {
        id: result.id,
        email: result.email,
        username: result.username || data.email.split('@')[0],
        displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || result.username,
        firstName: data.firstName,
        lastName: data.lastName,
      },
    };
  } catch (error: any) {
    console.error('WooCommerce registration error:', error);
    return {
      success: false,
      message: error.message || 'An error occurred during registration',
    };
  }
}

/**
 * Verify if user registration is enabled in WordPress
 */
export async function checkRegistrationEnabled(): Promise<boolean> {
  try {
    const response = await fetch(`${WORDPRESS_URL}/wp-json/wp/v2/users`, {
      method: 'OPTIONS',
    });
    return response.ok;
  } catch {
    return false;
  }
}

